// Core Modules
import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

// External Modules
import { NgxPermissionsService } from 'ngx-permissions';
import { catchError, map, Observable } from 'rxjs';

// Services
import { ConfigService } from '@core/services/config/config.service';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';

// Mappers
import { clientMapper, engineerMapper, marketMapper, tokenMapper, userMapper } from '@core/permission-mappers/permission-mapper';

// Models
import { AppEndpointsModel } from '@shared/models/endpoints.config.model';
import { IUserDetails } from '@shared/models/auth-api-response.model';

// Enums
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';

@Injectable({
  providedIn: 'root',
})
export class AccessControlService {
  // Injectors
  private readonly configService = inject(ConfigService);
  private readonly errorHandlerService = inject(ErrorHandlerService);
  private readonly httpClient = inject(HttpClient);
  private readonly permissionsService = inject(NgxPermissionsService);

  // Cached /auth/me body — powers the account-dropdown badge and the My access & scopes modal with no extra request.
  private readonly _userDetails = signal<IUserDetails | null>(null);
  public readonly userDetails = this._userDetails.asReadonly();

  // Private Members
  private readonly applicationScopes: Record<string, string[]>;
  private readonly appEndpoints: AppEndpointsModel;

  constructor() {
    this.applicationScopes = { ...userMapper(), ...tokenMapper(), ...clientMapper(), ...marketMapper(), ...engineerMapper() };
    this.appEndpoints = this.configService.getEndpointsByModule('appEndpoints');
  }

  public definePermissions(): Observable<boolean> {
    return this.getUserScopes().pipe(
      map((resp: IUserDetails): boolean => {
        this._userDetails.set(resp);
        const currentAppPermissions = Object.keys(this.applicationScopes).filter((key) => this.applicationScopes[key].every((value: string) => resp.scopes.includes(value)));
        currentAppPermissions.push('admin-app-read');
        this.permissionsService.loadPermissions(currentAppPermissions);
        return true;
      }),
      catchError(this.errorHandlerService.errorHandler)
    );
  }

  public async getLandingPageBasedOnUserScopes(): Promise<string> {
    const userLandingPageBasedOnUserScopes: boolean = await this.permissionsService.hasPermission(['admin-app-read']);
    if (userLandingPageBasedOnUserScopes) {
      return `/${MainNavigationEnum.USERS}`;
    }
    return `/${MainNavigationEnum.UNAUTHORIZED}`;
  }

  public getUserScopes(): Observable<IUserDetails> {
    return this.httpClient.get<IUserDetails>(`${this.configService.getAPIBaseUrl}${this.appEndpoints.userScopes}`).pipe(
      map((data: IUserDetails) => data),
      catchError(this.errorHandlerService.errorHandler)
    );
  }
}
